import { useNavigate } from "react-router-dom";
import { Bookmark, Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ProductImage } from "@/components/catalog/ProductImage";
import { PagePlaceholder } from "@/components/common/PagePlaceholder";
import { useCart } from "@/features/cart/CartContext";
import { useAuth } from "@/features/auth/AuthContext";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";
import type { CartLine } from "@/features/cart/api";

function fINR(n: number) {
    return `₹${n.toLocaleString("en-IN")}`;
}

export default function Cart() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { cart, isLoading, add, setQuantity, remove, clear, saveForLater } =
        useCart();

    const onCheckout = () => {
        if (!isAuthenticated) {
            toast("Please sign in to continue", {
                description: "Your cart will be preserved.",
            });
            navigate(ROUTES.login, { state: { from: ROUTES.checkout } });
            return;
        }
        if (cart.items.length === 0) return;
        navigate(ROUTES.checkout);
    };

    return (
        <div data-testid="cart-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <div className="flex items-end justify-between gap-3">
                    <div>
                        <h1 className="tsgo-h1">Your cart</h1>
                        <p className="tsgo-lead mt-2">
                            {cart.total_items === 0
                                ? "Your cart is empty."
                                : `${cart.total_items} item${cart.total_items > 1 ? "s" : ""} in cart`}
                        </p>
                    </div>
                    {cart.items.length > 0 ? (
                        <button
                            type="button"
                            onClick={async () => {
                                await clear();
                                toast("Cart cleared");
                            }}
                            data-testid="cart-clear"
                            className="text-xs font-semibold text-error hover:underline"
                        >
                            Clear cart
                        </button>
                    ) : null}
                </div>
            </section>

            <section className="tsgo-container mt-8 grid gap-6 lg:grid-cols-[1fr_360px]">
                <div className="space-y-4">
                    {isLoading && cart.items.length === 0 ? (
                        <div className="animate-pulse rounded-2xl border border-border/70 bg-card p-6">
                            <div className="h-4 w-1/2 rounded-full bg-muted" />
                            <div className="mt-3 h-3 w-1/3 rounded-full bg-muted" />
                        </div>
                    ) : cart.items.length === 0 ? (
                        <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-6">
                            <PagePlaceholder
                                testId="cart-empty"
                                icon={<ShoppingCart className="h-6 w-6" />}
                                title="Your cart is empty"
                                description="Add products from Home or Categories to see them here."
                                actions={
                                    <Button
                                        onClick={() => navigate(ROUTES.home)}
                                        className="bg-brand text-white hover:bg-brand-600"
                                        data-testid="cart-browse"
                                    >
                                        Start shopping
                                    </Button>
                                }
                            />
                        </div>
                    ) : (
                        <ul
                            data-testid="cart-items"
                            className="divide-y divide-border/70 overflow-hidden rounded-2xl border border-border/70 bg-card"
                        >
                            {cart.items.map((line) => (
                                <CartItemRow
                                    key={line.product_id}
                                    line={line}
                                    onInc={async () => {
                                        try { await add(line.product_id, 1); }
                                        catch { toast.error("Couldn't update cart"); }
                                    }}
                                    onDec={async () => {
                                        try { await add(line.product_id, -1); }
                                        catch { toast.error("Couldn't update cart"); }
                                    }}
                                    onSetQty={async (q) => {
                                        try { await setQuantity(line.product_id, q); }
                                        catch { toast.error("Couldn't update cart"); }
                                    }}
                                    onRemove={async () => {
                                        try { await remove(line.product_id); }
                                        catch { toast.error("Couldn't remove item"); }
                                    }}
                                    onSave={isAuthenticated ? async () => {
                                        try { await saveForLater(line.product_id); }
                                        catch { toast.error("Couldn't save for later"); }
                                    } : undefined}
                                />
                            ))}
                        </ul>
                    )}

                    {cart.saved_for_later.length > 0 ? (
                        <SavedForLater />
                    ) : null}
                </div>

                <PriceSummary
                    subtotal={cart.subtotal}
                    savings={cart.total_savings}
                    onCheckout={onCheckout}
                    disabled={cart.items.length === 0}
                />
            </section>
        </div>
    );
}

function CartItemRow({
    line,
    onInc,
    onDec,
    onSetQty,
    onRemove,
    onSave,
}: {
    line: CartLine;
    onInc: () => void;
    onDec: () => void;
    onSetQty: (q: number) => void;
    onRemove: () => void;
    onSave?: () => void;
}) {
    const outOfStock = !line.in_stock;
    const discount = Math.max(
        0,
        Math.round(((line.mrp - line.price) / line.mrp) * 100),
    );
    return (
        <li
            data-testid={`cart-item-${line.product_id}`}
            className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4"
        >
            <ProductImage
                product={line}
                size="thumb"
                className="h-16 w-16 shrink-0 rounded-xl border border-border/60"
            />
            <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {line.brand}
                </p>
                <p className="line-clamp-2 text-sm font-semibold text-foreground">
                    {line.name}
                </p>
                <p className="text-xs text-muted-foreground">{line.weight}</p>
                {outOfStock ? (
                    <p className="mt-1 text-xs font-semibold text-error">
                        Currently out of stock
                    </p>
                ) : null}
                <div className="mt-2 flex flex-wrap items-center gap-3">
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-sm font-extrabold">{fINR(line.price)}</span>
                        {discount > 0 ? (
                            <>
                                <span className="text-[11px] text-muted-foreground line-through">
                                    {fINR(line.mrp)}
                                </span>
                                <span className="text-[11px] font-bold text-brand">
                                    {discount}% OFF
                                </span>
                            </>
                        ) : null}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {onSave ? (
                            <button
                                type="button"
                                onClick={onSave}
                                data-testid={`cart-save-${line.product_id}`}
                                className="inline-flex items-center gap-1 rounded-full border border-border/70 px-2.5 py-1 text-[11px] font-semibold text-foreground/70 hover:text-brand"
                            >
                                <Bookmark className="h-3 w-3" /> Save
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={onRemove}
                            data-testid={`cart-remove-${line.product_id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-error/40 px-2.5 py-1 text-[11px] font-semibold text-error hover:bg-error hover:text-white"
                        >
                            <Trash2 className="h-3 w-3" /> Remove
                        </button>
                        <div
                            data-testid={`cart-qty-${line.product_id}`}
                            className="grid h-8 grid-cols-3 items-center overflow-hidden rounded-full bg-brand text-white"
                            style={{ minWidth: 88 }}
                        >
                            <button
                                type="button"
                                onClick={onDec}
                                aria-label="Decrease"
                                className="h-full w-full transition hover:bg-brand-700"
                            >
                                <Minus className="mx-auto h-3.5 w-3.5" />
                            </button>
                            <input
                                aria-label="Quantity"
                                value={line.quantity}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    if (Number.isFinite(v)) onSetQty(v);
                                }}
                                className="h-full w-full bg-transparent text-center text-xs font-bold text-white outline-none"
                            />
                            <button
                                type="button"
                                onClick={onInc}
                                aria-label="Increase"
                                className="h-full w-full transition hover:bg-brand-700"
                            >
                                <Plus className="mx-auto h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </li>
    );
}

function SavedForLater() {
    const { cart, restore, remove } = useCart();
    return (
        <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-5" data-testid="cart-saved">
            <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
                Saved for later
            </h3>
            <ul className="mt-3 divide-y divide-border/70">
                {cart.saved_for_later.map((line) => (
                    <li key={line.product_id} className="flex items-center gap-3 py-2.5">
                        <ProductImage product={line} size="thumb" className="h-12 w-12 rounded-lg border border-border/60" />
                        <div className="min-w-0 flex-1">
                            <p className="line-clamp-1 text-sm font-semibold">{line.name}</p>
                            <p className="text-xs text-muted-foreground">{line.weight} · {fINR(line.price)}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => restore(line.product_id)}
                            className="rounded-full border border-brand/40 bg-brand/10 px-2.5 py-1 text-[11px] font-semibold text-brand hover:bg-brand hover:text-white"
                        >
                            Move to cart
                        </button>
                        <button
                            type="button"
                            onClick={() => remove(line.product_id)}
                            className="text-[11px] text-error hover:underline"
                        >
                            Remove
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function PriceSummary({
    subtotal,
    savings,
    onCheckout,
    disabled,
}: {
    subtotal: number;
    savings: number;
    onCheckout: () => void;
    disabled: boolean;
}) {
    return (
        <aside className="h-fit rounded-2xl border border-border/70 bg-card p-5 shadow-soft" data-testid="cart-summary">
            <h3 className="tsgo-h3">Cart summary</h3>
            <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Subtotal</dt><dd className="font-semibold">{fINR(subtotal)}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">You save</dt><dd className="font-semibold text-brand">{fINR(savings)}</dd></div>
                <div className="my-3 border-t border-border/70" />
                <div className="flex items-center justify-between text-base">
                    <dt className="font-semibold">Subtotal</dt>
                    <dd className="text-lg font-extrabold" data-testid="cart-subtotal">{fINR(subtotal)}</dd>
                </div>
                <p className="text-xs text-muted-foreground">Delivery, fees & taxes calculated at checkout.</p>
            </dl>
            <Button
                onClick={onCheckout}
                disabled={disabled}
                data-testid="cart-checkout"
                className={cn("mt-5 h-11 w-full rounded-full text-sm font-semibold", "bg-brand text-white hover:bg-brand-600 shadow-brand")}
            >
                Proceed to checkout
            </Button>
        </aside>
    );
}
